import React, { useEffect, useState } from "react";
import {
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Select,
  FormLabel,
  CircularProgress,
  Box,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import HeadrailConfig from "../config/HeadrailConfigs";
import CreateCategoryModal from "../Category/CreateCategoryModal";
import { getAllCategories } from "../../../redux/actions/InventoryAction";

const ItemDetailsForm = ({ formData, handleChange }) => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userSignIn);
  const { categories, loading, error } = useSelector((state) => state.getCategories);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (userInfo?.token) {
      dispatch(getAllCategories(userInfo.token));
    }
  }, [dispatch, userInfo]);

  const handleCategoryChange = (e) => {
    if (e.target.value === "create") {
      setModalOpen(true);
    } else {
      handleChange(e);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    if (!formData.category) {
      handleChange({ target: { name: "category", value: "" } });
    }
  };

  const renderConfigComponent = () => {
    const categoryName = categories?.find((cat) => cat.value === formData.category)?.name;
    switch (categoryName) {
      case "Headrail":
        return <HeadrailConfig formData={formData} handleChange={handleChange} />;
      default:
        return null;
    }
  };

  if (error) return <div style={{ color: "white", textAlign: "center" }}>Error: {error}</div>;

  return (
    <>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 px-4">
            <div
              className="card shadow-none border"
              style={{ padding: "20px", maxWidth: "100%", backgroundColor: "#1a2a44" }}
            >
              <div className="card-body">
                <h5 className="card-title mb-4 text-success">Item Detail</h5>
                <div className="row g-3">
                  {/* Item Category */}
                  <div className="col-12">
                    <FormLabel sx={{ color: "white", mb: 1 }}>
                      Item Category
                      <Tooltip
                        title={
                          <span style={{ fontSize: ".8rem" }}>
                            Select the{" "}
                            <span style={{ color: "green" }} className="text-success">
                              Category
                            </span>{" "}
                            to which the item belongs.
                          </span>
                        }
                        arrow
                      >
                        <IconButton size="small" sx={{ color: "white" }}>
                          <HelpOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </FormLabel>
                    <Select
                      fullWidth
                      name="category"
                      value={loading ? "" : formData.category || ""}
                      onChange={handleCategoryChange}
                      displayEmpty
                      disabled={loading}
                      sx={{
                        color: "white",
                        bgcolor: "#2a3a5a",
                        "& .MuiSelect-icon": { color: "white" },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                        "&.Mui-disabled": {
                          color: "white",
                          "& .MuiSelect-icon": { color: "white" },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                        },
                      }}
                      renderValue={(value) =>
                        loading ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={20} sx={{ color: "white" }} />
                            <span>Loading...</span>
                          </Box>
                        ) : value ? (
                          categories?.find((cat) => cat.value === value)?.name || "Select Category"
                        ) : (
                          "Select Category"
                        )
                      }
                    >
                      <MenuItem value="" disabled sx={{ color: "black" }}>
                        Select Category
                      </MenuItem>
                      {categories?.map((cat) => (
                        <MenuItem key={cat.value} value={cat.value} sx={{ color: "black" }}>
                          {cat.name}
                        </MenuItem>
                      ))}
                      <MenuItem value="create" sx={{ color: "black" }}>
                        <AddIcon sx={{ mr: 1, color: "black" }} /> Create Category
                      </MenuItem>
                    </Select>
                  </div>

                  {/* Item Name */}
                  {formData.category && (
                    <div className="col-12">
                      <FormLabel sx={{ color: "white", mb: 1 }}>
                        Item Name
                        <Tooltip
                          title={
                            <span style={{ fontSize: ".8rem" }}>
                              Enter the <span className="text-success">name</span> of the item.
                            </span>
                          }
                          arrow
                        >
                          <IconButton size="small" sx={{ color: "white" }}>
                            <HelpOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </FormLabel>
                      <TextField
                        fullWidth
                        placeholder="Item Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        sx={{
                          "& .MuiInputBase-input": { color: "white" },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                          bgcolor: "#2a3a5a",
                        }}
                      />
                    </div>
                  )}

                  {formData.name && renderConfigComponent()}
                  <div className="col-md-6 mt-4">
                    <FormLabel sx={{ color: "white", mb: 1 }}>Deduction Method</FormLabel>
                    <Select
                      fullWidth
                      name="deductionMethod"
                      value={formData.deductionMethod}
                      onChange={handleChange}
                      displayEmpty
                      sx={{
                        color: "white",
                        bgcolor: "#2a3a5a",
                        "& .MuiSelect-icon": { color: "white" },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                      }}
                      renderValue={(value) => value || "Select Deduction Method"}
                    >
                      <MenuItem value="" disabled sx={{ color: "black" }}>
                        Select Deduction Method
                      </MenuItem>
                      <MenuItem value="width" sx={{ color: "black" }}>Width</MenuItem>
                      <MenuItem value="height" sx={{ color: "black" }}>Height</MenuItem>
                      <MenuItem value="sqft" sx={{ color: "black" }}>Square Feet (sqft)</MenuItem>
                    </Select>
                  </div>
                  {(formData.sleeveOn || formData.sleeveOff || formData.category !== "Headrail") && (
                    <div className="col-12">
                      <FormLabel sx={{ color: "white", mb: 1 }}>
                        Item Remarks/Description
                        <Tooltip
                          title={
                            <span style={{ fontSize: ".8rem" }}>
                              Provide a <span className="text-success">description</span> or any{" "}
                              <span className="text-success">remarks</span> about the item.
                            </span>
                          }
                          arrow
                        >
                          <IconButton size="small" sx={{ color: "white" }}>
                            <HelpOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </FormLabel>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        placeholder="Specify the item and how it's been used"
                        name="des"
                        value={formData.des}
                        onChange={handleChange}
                        sx={{
                          "& .MuiInputBase-input": { color: "white" },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                          bgcolor: "#2a3a5a",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CreateCategoryModal
        open={modalOpen}
        onClose={handleModalClose}
        token={userInfo?.token}
      />
    </>
  );
};

export default ItemDetailsForm;